<script>
import api from '@/api'
import appConfig from '@/app.config'
import { createQuery } from '@/data/hostSummaryQuery'

const METRICS_LOG = '[DeviceDashboardMetrics]'
const METRICS_CACHE_TTL_MS = 60000
const HOVER_DEBOUNCE_MS = 200

/**
 * Dashboard Home page component
 */
export default {
  page: {
    title: 'Device Dashboard',
    meta: [
      {
        name: 'description',
        content: appConfig.description,
      },
    ],
  },
  data() {
    return {
      categories: [],
      devices: [],
      loading: true,
      fullscreenEl: null,
      metricsCache: {},
      activeHoverHost: null,
      hoverDebounceTimer: null,
      hoverAbortController: null,
    }
  },
  mounted() {
    this.getDevices()
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
  },
  beforeUnmount() {
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    this.cancelHoverMetrics('unmount')
    this.resetTooltipContainer()
  },

  computed: {
    apiKey() {
      return this.$store.state.auth.user?.accessToken;
    }
  },

  methods: {
    toggleCategory(category) {
      category.visible = !category.visible

      // Get current deviceCategories from localStorage (comma-separated string)
      let stored = localStorage.getItem('deviceCategories') || ''
      let deviceCategories = stored ? stored.split(',').map((id) => parseInt(id)) : []

      if (category.visible) {
        // Add the ID if it's not already included
        if (!deviceCategories.includes(category.id)) {
          deviceCategories.push(category.id)
          localStorage.setItem('deviceCategories', deviceCategories.join(','))
          this.getDevices()
        }
      } else {
        // Remove the ID if it's there
        deviceCategories = deviceCategories.filter((id) => id !== category.id)
        localStorage.setItem('deviceCategories', deviceCategories.join(','))
      }
    },

    getDevices() {
      this.loading = true

      const deviceCategories = localStorage.getItem('deviceCategories')
      const params = deviceCategories ? { categoryIds: deviceCategories } : {}

      api
        .get(`${window.apiUrl}/dashboard/category-devices`, { params })
        .then((response) => {
          const categories = response.data || []

          // First visit: expand only the first category to limit devices in view.
          if (!deviceCategories) {
            if (categories.length === 0) {
              this.categories = []
              return
            }

            const firstCategoryId = String(categories[0].id)
            localStorage.setItem('deviceCategories', firstCategoryId)

            return api
              .get(`${window.apiUrl}/dashboard/category-devices`, {
                params: { categoryIds: firstCategoryId },
              })
              .then((firstOnlyResponse) => {
                this.categories = firstOnlyResponse.data || []
              })
          }

          this.categories = categories
        })
        .catch((err) => {
          console.error(`${METRICS_LOG} failed to load categories`, err)
        })
        .finally(() => {
          this.loading = false
        })
    },

    goToAnalyticsDashboard(deviceId) {
      this.$router.push({ name: 'analytics-dashboard', params: { deviceId: deviceId } })
    },

    toggleFullscreen(refName) {
      const el = this.$refs[refName]

      // Handle arrays returned by v-for refs
      const target = Array.isArray(el) ? el[0] : el

      if (target && typeof target.requestFullscreen === 'function') {
        if (!document.fullscreenElement) {
          target.requestFullscreen()
          this.fullscreenEl = refName
        } else {
          document.exitFullscreen()
        }
      }
    },

    onFullscreenChange() {
      if (!document.fullscreenElement) {
        this.fullscreenEl = null
        this.resetTooltipContainer()
      }
    },

    resetTooltipContainer() {
      const tooltip = document.getElementById('device-tooltip')
      if (tooltip && this.$refs.tooltipHost && !this.$refs.tooltipHost.contains(tooltip)) {
        this.$refs.tooltipHost.appendChild(tooltip)
      }
    },

    ensureTooltipContainer(tooltip) {
      const fsEl = document.fullscreenElement
      if (fsEl && !fsEl.contains(tooltip)) {
        fsEl.appendChild(tooltip)
      } else if (!fsEl && this.$refs.tooltipHost && !this.$refs.tooltipHost.contains(tooltip)) {
        this.$refs.tooltipHost.appendChild(tooltip)
      }
    },

    escapeHtml(value) {
      if (!value) return ''
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
    },

    tooltipSectionHeader(title, isFirst = false) {
      const border = isFirst ? '' : 'border-top:1px solid rgba(255,255,255,0.2);'
      const padding = isFirst ? 'padding:0 6px 5px;' : 'padding:10px 6px 5px;'
      return `
        <tr class="tooltip-section-header">
          <td colspan="2" style="${padding} ${border} font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:0.5px; color:#94a3b8;">
            ${title}
          </td>
        </tr>`
    },

    tooltipRow(label, value) {
      if (value === undefined || value === null || String(value).trim() === '') return ''
      return `
        <tr>
          <td style="padding:3px 6px;"><strong>${label}</strong></td>
          <td style="padding:3px 6px;">${this.escapeHtml(value)}</td>
        </tr>`
    },

    buildLicenseInfoTooltipRows(device) {
      if (!device) return ''

      const license = device.licenseInfo || {}

      return [
        this.tooltipRow('Account Number', device.accountNumber),
        this.tooltipRow('Setup PIN', license.pin),
        this.tooltipRow('Started At', license.startedAt),
        this.tooltipRow('Expiration', license.expiresAt),
        this.tooltipRow('License Duration', license.licensePeriodDisplay),
        this.tooltipRow('Licensed Users', license.licensedUsersDisplay),
        this.tooltipRow('Device Type', device.deviceType),
        this.tooltipRow('VPN IP', license.ip),
        this.tooltipRow('Description', license.description || device.description3),
        this.tooltipRow('Product', license.product),
        this.tooltipRow('Remarks', license.remarks || device.description2),
      ].join('')
    },

    /*showTooltip(device, event) {
      const tooltip = document.getElementById("device-tooltip");

      let statusClass = "";
      switch (device.status.toLowerCase()) {
        case "critical": statusClass = "tooltip-critical"; break;
        case "trouble": statusClass = "tooltip-trouble"; break;
        case "attention": statusClass = "tooltip-attention"; break;
        case "clear": statusClass = "tooltip-clear"; break;
        case "maintenance": statusClass = "tooltip-maintenance"; break;
        case "offline": statusClass = "tooltip-offline"; break;
        case "awaiting_connection": statusClass = "tooltip-awaiting_connection"; break;
      }

      tooltip.className = "device-tooltip " + statusClass;

      tooltip.innerHTML = `
        <div><strong>Host:</strong> ${device.host}</div>
        <div><strong>Status:</strong> ${device.status}</div>
        <div><strong>Category:</strong> ${device.categoryName}</div>
        <div><strong>Device Type:</strong> ${device.deviceType}</div>
        <div><strong>Credential:</strong> ${device.credentialName}</div>
      `;

      const hostEl = event.currentTarget.querySelector(".device-host");
      const rect = hostEl.getBoundingClientRect();

      tooltip.style.left = rect.left + "px";
      tooltip.style.top = rect.bottom + 4 + "px"; // 4px below host
      tooltip.style.display = "block";
    },*/

    cancelHoverMetrics(reason = 'leave') {
      if (this.hoverDebounceTimer !== null) {
        clearTimeout(this.hoverDebounceTimer)
        this.hoverDebounceTimer = null
      }

      if (this.hoverAbortController) {
        const host = this.activeHoverHost
        console.log(`${METRICS_LOG} abort`, { host, reason })
        this.hoverAbortController.abort()
        this.hoverAbortController = null
      }

      this.activeHoverHost = null
    },

    getCachedMetrics(host) {
      const cached = this.metricsCache[host]
      if (!cached) return null
      if (Date.now() - cached.fetchedAt > METRICS_CACHE_TTL_MS) {
        delete this.metricsCache[host]
        return null
      }
      return cached.metrics
    },

    emptyMetrics() {
      return {
        cpuIdle: 0,
        cpuUsed: null,
        memoryUsed: null,
        diskUsed: null,
        lastSeen: null,
      }
    },

    metricBarRow(label, value, metricsState) {
      if (metricsState === 'loading') {
        return `
          <tr>
            <td style="padding:3px 6px;"><strong>${label}</strong></td>
            <td style="padding:3px 6px;">Loading…</td>
          </tr>`
      }

      const numeric = value === undefined || value === null || value === '' || value === 'NA' ? null : Number(value)
      const width = numeric == null || Number.isNaN(numeric) ? 0 : Math.max(0, Math.min(100, numeric))

      return `
        <tr>
          <td style="padding:3px 6px;"><strong>${label}</strong></td>
          <td style="padding:3px 6px;">
            <div style="display:flex; align-items:center; gap:8px;">
              <div style="
                  width:90px;
                  height:4px;
                  background:#e0e0e0;
                  border-radius:3px;
                  overflow:hidden;
              ">
                <div style="
                    width:${width}%;
                    height:100%;
                    background:${this.getBarColor(width)};
                    border-radius:3px;
                "></div>
              </div>
              <span>${this.formatPercent(value)}</span>
            </div>
          </td>
        </tr>`
    },

    renderDeviceTooltip(device, metrics, metricsState) {
      const tooltip = document.getElementById('device-tooltip')
      if (!tooltip) return

      this.ensureTooltipContainer(tooltip)

      let statusClass = ''
      switch ((device.status || '').toLowerCase()) {
        case 'critical': statusClass = 'tooltip-critical'; break
        case 'trouble': statusClass = 'tooltip-trouble'; break
        case 'attention': statusClass = 'tooltip-attention'; break
        case 'clear': statusClass = 'tooltip-clear'; break
        case 'maintenance': statusClass = 'tooltip-maintenance'; break
        case 'offline': statusClass = 'tooltip-offline'; break
        case 'awaiting_connection': statusClass = 'tooltip-awaiting_connection'; break
      }

      tooltip.className = 'device-tooltip ' + statusClass

      const licenseRows = this.authUserIsEzemanage ? this.buildLicenseInfoTooltipRows(device) : ''
      const lastSeenValue =
        metricsState === 'loading' ? 'Loading…' : this.formatTimeAgo(metrics?.lastSeen)

      tooltip.innerHTML = `
      <table style="
          border-collapse: collapse;
          width: 100%;
          font-size: 13px;
          color: #d4d4d4;
      ">

        ${this.tooltipSectionHeader('Device', true)}

        <tr>
          <td style="padding:3px 6px;"><strong>Host</strong></td>
          <td class="text-uppercase" style="padding:3px 6px;">${device.host}</td>
        </tr>
        <tr>
          <td style="padding:3px 6px;"><strong>IP Address</strong></td>
          <td style="padding:3px 6px;">${device.ipAddress || '—'}</td>
        </tr>

        <tr>
          <td style="padding:3px 6px;"><strong>Status</strong></td>
          <td style="padding:3px 6px;">${device.status}</td>
        </tr>

        <tr>
          <td style="padding:3px 6px;"><strong>Category</strong></td>
          <td style="padding:3px 6px;">${device.categoryName}</td>
        </tr>

        ${!this.authUserIsEzemanage ? `
        <tr>
          <td style="padding:3px 6px;"><strong>Device Type</strong></td>
          <td style="padding:3px 6px;">${device.deviceType}</td>
        </tr>` : ''}

        <tr>
          <td style="padding:3px 6px;"><strong>Credential</strong></td>
          <td style="padding:3px 6px;">${device.credentialName}</td>
        </tr>

        ${licenseRows ? this.tooltipSectionHeader('License') + licenseRows : ''}

        ${this.tooltipSectionHeader('Metrics')}

        <tr>
          <td style="padding:3px 6px;"><strong>Last Seen</strong></td>
          <td style="padding:3px 6px;">${lastSeenValue}</td>
        </tr>

        ${this.metricBarRow('CPU Used', metrics?.cpuUsed, metricsState)}
        ${this.metricBarRow('Memory Used', metrics?.memoryUsed, metricsState)}
        ${this.metricBarRow('Disk Usage', metrics?.diskUsed, metricsState)}

      </table>
    `
    },

    positionTooltip(event) {
      const tooltip = document.getElementById('device-tooltip')
      if (!tooltip || !event?.currentTarget) return

      const hostEl = event.currentTarget.querySelector('.device-host')
      if (!hostEl) return

      const rect = hostEl.getBoundingClientRect()
      tooltip.style.left = rect.left + 'px'
      tooltip.style.top = rect.bottom + 4 + 'px'
      tooltip.style.display = 'block'
    },

    showTooltip(device, event) {
      try {
        const host = device?.host
        if (!host) return

        this.cancelHoverMetrics('superseded')
        this.activeHoverHost = host

        const cached = this.getCachedMetrics(host)
        if (cached) {
          console.log(`${METRICS_LOG} cache hit`, { host })
          this.renderDeviceTooltip(device, cached, 'ready')
          this.positionTooltip(event)
          return
        }

        this.renderDeviceTooltip(device, this.emptyMetrics(), 'loading')
        this.positionTooltip(event)

        this.hoverDebounceTimer = setTimeout(() => {
          this.hoverDebounceTimer = null
          this.fetchHostMetrics(device)
        }, HOVER_DEBOUNCE_MS)
      } catch (err) {
        console.error(`${METRICS_LOG} showTooltip failed`, err)
      }
    },

    resolveHostMetrics(parsed, host) {
      if (!parsed || typeof parsed !== 'object') return this.emptyMetrics()
      if (parsed[host]) return parsed[host]

      const matchKey = Object.keys(parsed).find(
        (key) => key.toLowerCase() === String(host).toLowerCase()
      )
      return matchKey ? parsed[matchKey] : this.emptyMetrics()
    },

    async fetchHostMetrics(device) {
      const host = device?.host
      if (!host || this.activeHoverHost !== host) return

      const controller = new AbortController()
      this.hoverAbortController = controller
      const startedAt = Date.now()

      console.log(`${METRICS_LOG} request start`, { host })

      try {
        const query = createQuery(host)
        const response = await api.post('/analytics/query/device-dashboard', query, {
          headers: {
            Authorization: 'Bearer ' + this.apiKey,
            'Content-Type': 'application/vnd.flux',
          },
          signal: controller.signal,
          skipErrorAlert: true,
        })

        if (this.activeHoverHost !== host) {
          console.log(`${METRICS_LOG} ignore stale response`, { host, active: this.activeHoverHost })
          return
        }

        const rows = Array.isArray(response.data) ? response.data : []
        const parsed = this.parseDeviceMetrics(rows)
        const metrics = this.resolveHostMetrics(parsed, host)

        this.metricsCache[host] = {
          metrics,
          fetchedAt: Date.now(),
        }

        console.log(`${METRICS_LOG} success`, {
          host,
          durationMs: Date.now() - startedAt,
          rowCount: rows.length,
        })

        this.renderDeviceTooltip(device, metrics, 'ready')
      } catch (err) {
        if (err?.cancelled || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
          console.log(`${METRICS_LOG} request cancelled`, { host })
          return
        }

        console.error(`${METRICS_LOG} request failed`, { host, error: err?.message || err })

        if (this.activeHoverHost === host) {
          this.renderDeviceTooltip(device, this.emptyMetrics(), 'error')
        }
      } finally {
        if (this.hoverAbortController === controller) {
          this.hoverAbortController = null
        }
      }
    },

    getBarColor(value) {
      if (value < 70) {
        return "linear-gradient(90deg, #4ade80, #22c55e)"; // green
      } else if (value < 90) {
        return "linear-gradient(90deg, #fb923c, #f97316)"; // orange
      } else {
        return "linear-gradient(90deg, #ef4444, #dc2626)"; // red
      }
    },

    formatPercent(value) {
      if (value === undefined || value === null || value === "" || value === "NA") {
        return "NA";
      }
      return `${value}%`;
    },

    hideTooltip() {
      this.cancelHoverMetrics('leave')
      const tooltip = document.getElementById("device-tooltip");
      if (tooltip) {
        tooltip.style.display = "none";
      }
    },

    parseCsv(csvText) {
       if (!csvText || typeof csvText !== "string") return [];
      const lines = csvText
        .trim()
        .split("\n")
        .filter(line => line.trim().length > 0);

      if (lines.length === 0) return [];

      const headers = lines[0].split(",").map(h => h.trim());

      let rows = [];

      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",");
        let obj = {};

        headers.forEach((header, idx) => {
          obj[header] = cols[idx] !== undefined ? cols[idx].trim() : "";
        });

        rows.push(obj);
      }

      return rows; 
    },  
    
    parseDeviceMetrics(rawData) {
      const devices = {};
      if (!Array.isArray(rawData)) return devices;

      // These measurements store the monitored device name in agent_host, not host
      const AGENT_HOST_MEASUREMENTS = ['ezelink_uptime_value', 'snmp', 'hr_storage', 'cpu_system_stats'];

      rawData.forEach(row => {
        const useAgentHost = AGENT_HOST_MEASUREMENTS.includes(row._measurement);
        const host = (useAgentHost && row.agent_host) ? row.agent_host : row.host;
        if (!host) return;

        if (!devices[host]) {
          devices[host] = {
            cpuIdle: 0,
            cpuUsed: 0,
            memoryUsed: 0,
            diskUsed: 0,
            lastSeen: null
          };
        }

        const device = devices[host];
        const value = parseFloat(row._value) || 0;

        switch (row._measurement) {

          // ******** CPU ********
          case "ezelink_cpu_value":
            if (row.type_instance === "idle") {
              device.cpuIdle = value;
            }
            break;

          // ******** MEMORY ********
          case "ezelink_memory_value":
            if (row.type === "percent" && row.type_instance === "used") {
              device.memoryUsed = value;   
            }
            break;

          // ******** DISK ********
          case "ezelink_df_value":
            if (row.type === "percent_bytes") {
              device.diskUsed = value;
            }
            break;

          // ******** LAST SEEN ********
          case "ezelink_uptime_value":
            device.lastSeen = row._time;   
            break;

          //******** LAST SEEN && MEMORY USED SNMP ********  
          case "snmp":
            if(row._field === "sysUpTime"){
              device.lastSeen = row._time;
            }
            if(row._field === "mem_used_percentage"){
              device.memoryUsed = value;
            }
            break;
            
          //******** DISK SNMP ********  
          case "hr_storage":
            if(row._field === "storage_usage_percentage"){
              device.diskUsed = value;
            }
            break; 
            
          //******** CPU USED SNMP ********  
          case "cpu_system_stats":
            if(row._field === "cpu_idle"){
              device.cpuIdle = value;
            }
            break;   
        }
      });

      // Final computed metrics
      Object.keys(devices).forEach(host => {
        const d = devices[host];

        d.cpuUsed = (100 - d.cpuIdle).toFixed(2);
        d.memoryUsed = d.memoryUsed.toFixed(2);
        d.diskUsed = d.diskUsed.toFixed(2);

        // Convert RAW timestamp to readable local time
        if (d.lastSeen) {
          d.lastSeen = new Date(d.lastSeen).toLocaleString();
        }
      });

      return devices;
    },

    formatTimeAgo(timestamp) {
      if (!timestamp) return "NA";

      const reported = new Date(timestamp);
      const now = new Date();

      const diffMs = now - reported;
      const diffSec = Math.floor(diffMs / 1000);

      if (diffSec < 60) return `${diffSec}s ago`;

      const diffMin = Math.floor(diffSec / 60);
      if (diffMin < 60) return `${diffMin}m ago`;

      const diffHr = Math.floor(diffMin / 60);
      if (diffHr < 24) return `${diffHr}h ago`;

      const diffDay = Math.floor(diffHr / 24);
      if (diffDay < 7) return `${diffDay}d ago`;

      const diffWeek = Math.floor(diffDay / 7);
      if (diffWeek < 4) return `${diffWeek}w ago`;

      const diffMonth = Math.floor(diffDay / 30);
      if (diffMonth < 12) return `${diffMonth}mo ago`;

      const diffYr = Math.floor(diffDay / 365);
      return `${diffYr}y ago`;
    },

  },
}
</script>

<template>
  <div ref="tooltipHost">
    <Teleport to="#elePageTitle">Device Dashoard</Teleport>
    <div class="card">
      <div class="card-body">
    <div v-for="category in categories" :key="category.id" :class="fullscreenEl === 'category_' + category.id ? 'p-3' : 'pb-3'" :ref="'category_' + category.id">
      <h5 class="cursor-pointer mb-3 d-flex justify-content-between align-center">
        <div @click="toggleCategory(category)" class="cursor-pointer">
          <span v-if="category.visible">-</span>
          <span v-else>+</span>
          <span class="ms-1">{{ category.categoryName }}</span>
        </div>
        <span v-if="category.visible" @click="toggleFullscreen('category_' + category.id)" class="cursor-pointer">
          <i v-if="fullscreenEl === 'category_' + category.id" class="bx bx-exit-fullscreen"></i>
          <i v-else class="bx bx-fullscreen"></i>
        </span>
      </h5>
      <div v-show="category.visible">
        <div v-if="category.devices.length" class="d-flex flex-wrap gap-4">
          <div v-for="device in category.devices" :key="device.id">

            <!-- CRITICAL -->
            <div v-if="device.status === 'critical'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip" 
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box critical"><i class="mdi mdi-alert-rhombus"></i></div>

              <div class="text-box w-100 critical">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- TROUBLE -->
            <div v-if="device.status === 'trouble'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box trouble"><i class="mdi mdi-alert-circle"></i></div>

              <div class="text-box w-100 trouble">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- ATTENTION -->
            <div v-if="device.status === 'attention'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box attention"><i class="mdi mdi-alert"></i></div>

              <div class="text-box w-100 attention">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- CLEAR -->
            <div v-if="device.status === 'clear'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box clear"><i class="mdi mdi-check-circle"></i></div>

              <div class="text-box w-100 clear">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- MAINTENANCE -->
            <div v-if="device.status === 'maintenance'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box maintenance"><i class="mdi mdi-wrench"></i></div>

              <div class="text-box w-100 maintenance">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- OFFLINE -->
            <div v-if="device.status === 'offline'" class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box offline"><i class="mdi mdi-wifi-off"></i></div>

              <div class="text-box w-100 offline">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
              </div>
            </div>

            <!-- CONNECTING -->
            <div v-if="device.status === 'Connecting' || device.status === 'awaiting_connection'"
                class="d-flex device-card device-card-hover"
                @mouseenter="showTooltip(device, $event)"
                @mouseleave="hideTooltip"
                @click="goToAnalyticsDashboard(device.id)">
              <div class="icon-box awaiting_connection"><i class="mdi mdi-wifi"></i></div>

              <div class="text-box w-100 awaiting_connection">
                <div class="device-host">{{ device.host.length > 14 ? device.host.slice(0, 14) + '..' : device.host }}</div>
                <!-- <div class="device-extra">
                  <span class="extra-label-awaiting_connection">Status:</span>
                  <span class="extra-value">{{ device.status }}</span>
                </div>

                <div class="device-extra">
                  <span class="extra-label-awaiting_connection">Category:</span>
                  <span class="extra-value">{{ device.categoryName }}</span>
                </div>

                <div class="device-extra">
                  <span class="extra-label-awaiting_connection">Device Type:</span>
                  <span class="extra-value">{{ device.deviceType }}</span>
                </div>

                <div class="device-extra">
                  <span class="extra-label-awaiting_connection">Credential:</span>
                  <span class="extra-value">{{ device.credentialName }}</span>
                </div> -->
              </div>
            </div>

          </div>
        </div>
        <div v-else>No devices found</div>
      </div>
    </div>
  </div>
</div>
    <div id="device-tooltip" class="device-tooltip"></div>
  </div>
</template>

<style scoped>
.device-tooltip {
  position: fixed;       
  min-width: 300px;
  padding: 10px 14px;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  line-height: 1.4;
  background: rgba(0,0,0,0.85);
  box-shadow: 0 2px 10px rgba(0,0,0,0.25);
  display: none;
  z-index: 9999;
}
.device-tooltip div {
  margin-bottom: 3px;
}
.device-tooltip div:last-child {
  margin-bottom: 0;
}
.device-tooltip strong {
  font-weight: 700;
}

.device-tooltip .tooltip-section-header td {
  margin-top: 2px;
}

.tooltip-critical    { 
  background: #22354e; 
  border: 3px solid #b71322; 
}    
.tooltip-trouble     { 
  background: #22354e; 
  border: 3px solid #db8f0e; 
}    
.tooltip-attention   { 
  background: #22354e; 
  border: 3px solid #1f6acd; 
}    
.tooltip-clear       { 
  background: #22354e; 
  border: 3px solid #1fa75b; 
}    
.tooltip-maintenance { 
  background: #22354e; 
  border: 3px solid #9d866d; 
}    
.tooltip-offline     { 
  background: #22354e; 
  border: 3px solid #59677D; 
}    
.tooltip-awaiting_connection  { 
  background: #22354e; 
  border: 3px solid #253a55; 
}  

.device-card {
  width: 200px !important;
  /* padding: 0.4rem 0.5rem; */
  border: 1px solid transparent;
  border-radius: 0.3rem;
  cursor: pointer;
  transition: filter .25s ease, box-shadow .25s ease;
}

.device-card:hover {
  filter: brightness(1.25);            /* Makes it brighter */
  box-shadow: 0 4px 12px rgba(0,0,0,0.25);
  cursor: pointer;
  z-index: 5;
  transform: scale(1.02);
}

.device-card .icon-box {
  padding: 10px;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  color: white;
} 

.device-card .icon-box.critical {
  background-color: rgb(183 19 34);
}
.device-card .icon-box.clear {
  background-color: rgb(30 145 48);
}
.device-card .icon-box.trouble {
  background-color: #db8f0e;
}
.device-card .icon-box.attention {
  background-color: #1f6acd;
}
.device-card .icon-box.offline {
  background-color: rgb(89 103 125);
}
.device-card .icon-box.maintenance {
  background-color: #9d866d;
}
.device-card .icon-box.awaiting_connection {
  background-color: #253a55;
}

.device-card .text-box {
  padding: 9px;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  border-left: 0;
  color: #141b2b;
  background-color: white;
  white-space: nowrap;
}

.device-card .text-box.critical {
  border: 1px solid rgb(183 19 34);
}
.device-card .text-box.clear {
  border: 1px solid rgb(30 145 48);
}
.device-card .text-box.trouble {
  border: 1px solid #db8f0e;
}
.device-card .text-box.attention {
  border: 1px solid #1f6acd;
}
.device-card .text-box.offline {
  border: 1px solid rgb(89 103 125);
}
.device-card .text-box.maintenance {
  border: 1px solid #9d866d;
}
.device-card .text-box.awaiting_connection {
  border: 1px solid #253a55;
}

.icon-box {
  display: flex;
  justify-content: center;  
  align-items: center;      
}

.extra-label {
  color: #cccccc;         
  font-weight: 600;
}

.extra-value {
  color: #ffffff;         
  margin-left: 3px;
}

.extra-label-critical {
  color: rgb(183 19 34) !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-clear {
  color: rgb(30 145 48) !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-trouble {
  color: #db8f0e !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-attention {
  color: #1f6acd !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-offline {
  color: rgb(89 103 125) !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-maintenance {
  color: #9d866d !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.extra-label-awaiting_connection {
  color: #253a55 !important; 
  margin-left: 3px;
  font-weight: bold; 
}

.device-host {
  text-transform: uppercase;
}

body[data-layout-mode='dark'] .device-card .text-box {
  color: #d9e3f3;
  background-color: transparent;
}

body[data-layout-mode='dark'] .card .card-body .p-3 {
  background-color: #22354e;
}

body[data-layout-mode='light'] .card .card-body .p-3 {
  background-color: #ffffff;
}
</style>
